import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import DropdownWithSearch from "../../../components/common/DropdownWithSearch";

const emptyCoachForm = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
};

const getSchoolKey = (school) => {
  if (!school) return "";
  if (typeof school === "string") return school;
  return school.value || school.label || "";
};

const getSchoolLabel = (school) => {
  if (!school) return "";
  if (typeof school === "string") return school;
  return school.label || school.value || "";
};

export const HighSchoolCoachInfoCard = ({ handleOpenCoachContactModal }) => {
  return (
    <div className="bg-white shadow-lg py-4 rounded-lg border-gray-300 mt-4">
      <div className="w-full px-4">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl uppercase">High School Coach Info</h1>
          <button
            type="button"
            onClick={handleOpenCoachContactModal}
            className="inline-flex items-center gap-2 text-blue-600 transition hover:text-blue-700 text-sm"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
            <span className="font-medium text-sm">Edit</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const CoachContactModal = ({
  isOpen,
  onClose,
  schoolOptions,
  selectedSchool,
  onSchoolChange,
  coachContacts,
  onUpdate,
  isLoadingSchools = false,
  title = "Edit Coach Contact",
}) => {
  const [formData, setFormData] = useState(emptyCoachForm);

  useEffect(() => {
    const schoolKey = getSchoolKey(selectedSchool);
    if (!schoolKey) {
      setFormData(emptyCoachForm);
      return;
    }

    setFormData(coachContacts[schoolKey] || emptyCoachForm);
  }, [selectedSchool, coachContacts]);

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;

    if (field === "phoneNumber") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSchoolSelect = (schools) => {
    onSchoolChange(schools[0] || null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const phone = formData.phoneNumber;

    if (phone.length !== 10) {
      toast.error("Phone number must be exactly 10 digits.");
      return;
    }

    const schoolKey = getSchoolKey(selectedSchool);
    if (!schoolKey) {
      toast.error("Please select a high school.");
      return;
    }

    const nextContact = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phoneNumber: phone,
    };

    try {
      await onUpdate({
        schoolKey,
        data: nextContact,
      });
      toast.success("Coach details updated successfully!");
    } catch (err) {
      const errorMessage =
        err?.error ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to update coach details.";
      toast.error(errorMessage);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="bg-white rounded-md shadow-2xl w-full max-w-3xl h-[580px] p-0 relative"
    >
      <form onSubmit={handleSubmit} className="p-8">
        <h2 className="text-xl font-semibold text-black">{title}</h2>
        <div className="mt-4 rounded-lg bg-blue-100 px-4 py-3 text-blue-700">
          <p className="text-sm font-medium">
            <FontAwesomeIcon icon={faCircleInfo} /> Select a high school to view
            coach details
          </p>
        </div>

        <div className="mt-4">
          <DropdownWithSearch
            label="Select School"
            options={schoolOptions}
            selectedValues={selectedSchool ? [selectedSchool] : []}
            placeholder="Select School"
            onSelect={handleSchoolSelect}
            allowMultiple={false}
            isLoading={isLoadingSchools}
            containerClassName="relative w-full"
            buttonClassName="w-full flex justify-between items-center border border-gray-300 px-4 py-3 rounded-md bg-white"
          />
        </div>

        {selectedSchool && (
          <div className="mt-6">
            <p className="text-base font-semibold text-black">
              High School Name: {getSchoolLabel(selectedSchool)}
            </p>
            <div className="mt-3 border-b border-gray-300" />
          </div>
        )}

        {selectedSchool && (
          <>
            <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-black">
                  First Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange("firstName")}
                  placeholder="Enter first name"
                  className="w-full rounded border border-gray-300 px-4 py-3 text-base text-black focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-black">
                  Last Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange("lastName")}
                  placeholder="Enter last name"
                  className="w-full rounded border border-gray-300 px-4 py-3 text-base text-black focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>

            <div className="mt-6 md:w-[calc(50%-0.75rem)]">
              <label className="mb-1 block text-sm font-medium text-black">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={handleInputChange("phoneNumber")}
                placeholder="Enter phone number"
                className="w-full rounded border border-gray-300 px-4 py-3 text-base text-black focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div className="mt-12 flex justify-end gap-4">
              <button
                type="button"
                onClick={() => onClose()}
                className="rounded border border-gray-300 px-8 py-3 text-sm text-gray-700 transition hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="submit"
                className="rounded bg-red-500 px-8 py-3 text-sm font-medium text-white transition hover:bg-red-600"
              >
                Update
              </button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default CoachContactModal;
